import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  ALL_PERMISSIONS,
  FLOOR,
  MODULES,
  ROLES,
  STARTER_ROLES,
  def,
} from "@dentacore/shared-types";
import type {
  AccessOverrideView,
  AccessRoleView,
  Job,
  Permission,
  RoleHolderView,
  StaffMember,
  WhoCanView,
} from "@dentacore/shared-types";

import {
  archiveRole,
  createRole,
  endOverride,
  installStarterRoles,
  listRoles,
  moveToJobRoles,
  overridesReport,
  updateRole,
  whoCan,
} from "../api/access";
import type { OverridesReport } from "../api/access";
import { listStaff } from "../api/staff";
import { useAuth } from "../auth/auth-context";
import { useCan } from "../auth/use-can";
import { ShelfList } from "../inventory/shelf-list";
import type { ShelfRow } from "../inventory/shelf-list";
import p from "../plans/procedures.module.css";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { Modal } from "../ui/modal";
import { Pager } from "../ui/paging";
import {
  choosableIn,
  impactOf,
  jobMoveAffected,
  impliedBy,
  jobLabel,
  jobRole,
  moduleChoice,
  permissionsIn,
  rolePermissions,
  viewsIn,
  withChoice,
  withClosure,
} from "./access-levels";
import type { Affected, Impact } from "./access-levels";
import a from "./access.module.css";
import { PaneHead } from "./pane";
import { Toast } from "./toast";
import { describe, useWriter } from "./use-writer";
import type { Writer } from "./use-writer";
import { RequiredMark } from "../ui/required-mark";

const ADMIN = "administrator";
const FLOOR_SET: ReadonlySet<Permission> = new Set(FLOOR);
type Tab = "roles" | "exceptions" | "whocan";

/**
 * Settings → Roles and access — the prototype the client finalised on
 * 2026-10-04, built.
 *
 * **Treatment plans' shape** (TR1–TR9): the roles in a list card that pages to
 * fit the window, the chosen one opening in a panel beside it as a summary, and
 * **every change in a dialog** that says what it will do before Save. Roles are
 * the clinic's, defined once; they are *given* per person per location, on the
 * person's page (R-D6). Administrator heads the list, locked.
 *
 * Three tabs: the roles, the live exceptions, and **Who can…** — one action,
 * everyone, every location, and why.
 *
 * **Everything here is presentation** (S3): the server refuses every write
 * unless the caller holds `access.roles.manage` or `access.assign`, and nobody
 * may change their own access whatever this screen draws.
 */
export function AccessPane(): React.JSX.Element {
  const can = useCan();
  const { user } = useAuth();
  const manage = can("access.roles.manage");
  const assign = can("access.assign");
  const writer = useWriter();
  const { toast, dismiss, fail } = writer;

  const [tab, setTab] = useState<Tab>("roles");
  const [roles, setRoles] = useState<AccessRoleView[] | null>(null);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [report, setReport] = useState<OverridesReport | null>(null);

  const reload = useCallback(async () => {
    const [rows, people, live] = await Promise.all([
      listRoles(),
      listStaff(),
      assign ? overridesReport() : Promise.resolve(null),
    ]);
    setRoles(rows);
    setStaff(people);
    setReport(live);
  }, [assign]);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      listRoles(controller.signal),
      listStaff(controller.signal),
      assign ? overridesReport(controller.signal) : Promise.resolve(null),
    ])
      .then(([rows, people, live]) => {
        if (controller.signal.aborted) return;
        setRoles(rows);
        setStaff(people);
        setReport(live);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setRoles([]);
        fail(describe(cause, "Could not read the roles."));
      });
    return () => controller.abort();
  }, [assign, fail]);

  const tabs: { key: Tab; label: string; count?: number | undefined }[] = [
    { key: "roles", label: "Roles", count: roles === null ? undefined : roles.length + 1 },
    ...(assign
      ? [
          { key: "exceptions" as const, label: "Exceptions", count: report?.overrides.length },
          { key: "whocan" as const, label: "Who can…" },
        ]
      : []),
  ];

  return (
    <div className={a.page}>
      <PaneHead title="Roles and access" testId="access-title" />
      <p className={a.lead}>
        Roles are set up once for the clinic and given to each person per location, on their page in
        Staff.
      </p>

      <div className={a.tabs} role="tablist" aria-label="Roles and access">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? `${a.tab} ${a.tabOn}` : a.tab}
            onClick={() => setTab(t.key)}
            data-testid={`access-tab-${t.key}`}
          >
            {t.label}
            {t.count !== undefined && <span className={a.tabCount}>{t.count}</span>}
          </button>
        ))}
      </div>

      {roles === null ? (
        <p className={a.hint}>Reading&hellip;</p>
      ) : tab === "roles" ? (
        <RolesTab
          roles={roles}
          staff={staff}
          selfId={user?.id ?? null}
          manage={manage}
          assign={assign}
          writer={writer}
          reload={reload}
        />
      ) : tab === "exceptions" ? (
        <ExceptionsTab report={report} selfId={user?.id ?? null} writer={writer} reload={reload} />
      ) : (
        <WhoCanTab fail={fail} />
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </div>
  );
}

// ── shared pieces ─────────────────────────────────────────────────────────────

/**
 * A dialog drawn at the top of the document. The Settings pane is a size
 * container, and a container is the box `position: fixed` measures from — so a
 * dialog drawn inside it centres on the pane, which on a long person page is
 * below the window. On `body` it centres on the window, as every dialog should.
 */
export function InBody({ children }: { children: React.ReactNode }): React.JSX.Element {
  return createPortal(children, document.body);
}

/**
 * A list area as tall as the window has room for, so the list card pages
 * instead of the page scrolling (PF4). Measured every render, because a banner
 * above it comes and goes.
 */
function useFill(): [(element: HTMLDivElement | null) => void, number] {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    if (element === null) return undefined;
    const measure = (): void => {
      // Measured from the top of whatever scrolls, so scrolling does not move it.
      let scroller: HTMLElement | null = element.parentElement;
      while (scroller !== null && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY))
        scroller = scroller.parentElement;
      const offset = scroller === null ? window.scrollY : scroller.scrollTop;
      const room = Math.floor(
        window.innerHeight - (element.getBoundingClientRect().top + offset) - 32,
      );
      // **Too little room — a narrow window where the Settings menu sits above —
      // and the list grows with its rows instead**, as ShelfList does on a phone
      // (QA, 2026-10-04: at 1024px it was squeezed into a small box below the fold).
      const next = room < 420 ? 0 : room;
      setHeight((was) => (was === next ? was : next));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  });
  return [setElement, height];
}

/** Local pages for a list inside a dialog or a card. */
export function usePages<T>(
  items: readonly T[],
  size: number,
  noun: string,
  resetOn?: unknown,
): { shown: T[]; pager: React.JSX.Element | null } {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [resetOn]);
  const count = Math.max(1, Math.ceil(items.length / size));
  const current = Math.min(page, count - 1);
  const start = current * size;
  const shown = items.slice(start, start + size);
  return {
    shown,
    pager:
      count > 1 ? (
        <div className={a.pagerRow}>
          <Pager
            page={current}
            count={count}
            onPage={setPage}
            from={start + 1}
            to={start + shown.length}
            total={items.length}
            noun={noun}
          />
        </div>
      ) : null,
  };
}

function LockTag({ title }: { title: string }): React.JSX.Element {
  return (
    <span className={a.lockTag} title={title}>
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path
          fill="currentColor"
          d="M5 7V5a3 3 0 0 1 6 0v2h1v7H4V7h1zm1.5 0h3V5a1.5 1.5 0 0 0-3 0v2z"
        />
      </svg>
      Locked
    </span>
  );
}

/** "Saving changes this for 4 people: they gain …; lose …" — said before Save. */
export function ImpactLine({
  impact,
  lead,
}: {
  impact: Impact;
  lead: React.ReactNode;
}): React.JSX.Element {
  if (impact.gain.length === 0 && impact.lose.length === 0)
    return (
      <p className={a.impact} data-testid="access-impact">
        No change yet.
      </p>
    );
  const list = (labels: string[]): string =>
    labels
      .slice(0, 2)
      .map((label) => `“${label}”`)
      .join(", ") + (labels.length > 2 ? ` and ${String(labels.length - 2)} more` : "");
  return (
    <p className={a.impact} data-testid="access-impact">
      {lead}
      {impact.gain.length > 0 && <span className={a.gain}> gain {list(impact.gain)}</span>}
      {impact.gain.length > 0 && impact.lose.length > 0 && ";"}
      {impact.lose.length > 0 && <span className={a.lose}> lose {list(impact.lose)}</span>}.
    </p>
  );
}

/**
 * Who changes role **now** — said before Save whenever a job moves between
 * roles, because a job's role is what everyone with no role given holds
 * (R-D6a; Anthony, 2026-10-04: "show who is affected").
 */
export function AffectedList({ affected }: { affected: readonly Affected[] }): React.JSX.Element {
  if (affected.length === 0)
    return (
      <p className={a.impact} data-testid="access-affected">
        Nobody&rsquo;s access changes now — only people added from now on.
      </p>
    );
  const people = new Set(affected.map((x) => x.userId)).size;
  return (
    <div className={a.notice} data-testid="access-affected">
      <b>
        This changes access now for {people} {people === 1 ? "person" : "people"}
      </b>{" "}
      who have no role given at a location, so they hold their job&rsquo;s role there:
      <ul className={a.affected}>
        {affected.slice(0, 5).map((x) => (
          <li key={`${x.userId}:${x.branchName}`}>
            {x.name} · {locationShort(x.branchName)}: {x.from} → {x.to}
          </li>
        ))}
        {affected.length > 5 && <li>and {affected.length - 5} more</li>}
      </ul>
    </div>
  );
}

/** Each part of DentaCore and how much of it: Full, None, View or "3 of 5". */
export function LevelList({
  held,
  onPick,
}: {
  held: (permission: Permission) => boolean;
  onPick?: (module: string) => void;
}): React.JSX.Element {
  return (
    <ul className={a.levels} data-testid="access-levels">
      {MODULES.map((module) => {
        const all = choosableIn(module.key);
        const mine = all.filter(held);
        const choice = moduleChoice(held, module.key);
        const text =
          choice === "custom" ? `${String(mine.length)} of ${String(all.length)}` : capital(choice);
        const tone = choice === "full" ? a.levelFull : choice === "none" ? a.levelNone : "";
        const inner = (
          <>
            <span className={a.ellipsis}>{module.label}</span>
            <span className={`${a.levelText} ${tone}`}>
              {text}
              {onPick !== undefined && " ›"}
            </span>
          </>
        );
        return (
          <li key={module.key}>
            {onPick === undefined ? (
              <span className={a.level}>{inner}</span>
            ) : (
              <button
                type="button"
                className={a.level}
                onClick={() => onPick(module.key)}
                data-testid={`access-level-${module.key}`}
              >
                {inner}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

const capital = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);
export const locationShort = (name: string): string => name.split(" — ")[0] ?? name;

function holdSummary(holders: readonly RoleHolderView[]): string {
  const where = new Map<string, number>();
  for (const h of holders) where.set(h.branchName, (where.get(h.branchName) ?? 0) + 1);
  return [...where]
    .sort(([x], [y]) => x.localeCompare(y))
    .map(([name, n]) => `${String(n)} at ${locationShort(name)}`)
    .join(", ");
}

// ── Roles ─────────────────────────────────────────────────────────────────────

type Move = RoleHolderView & { target: AccessRoleView | undefined; self: boolean };

function RolesTab({
  roles,
  staff,
  selfId,
  manage,
  assign,
  writer,
  reload,
}: {
  roles: AccessRoleView[];
  staff: StaffMember[];
  selfId: string | null;
  manage: boolean;
  assign: boolean;
  writer: Writer;
  reload: () => Promise<void>;
}): React.JSX.Element {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    "allows" | "details" | "new" | "holders" | "move" | "starter" | null
  >(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [fillRef, height] = useFill();

  const admins = useMemo(() => staff.filter((s) => s.isActive && s.canAdminister), [staff]);
  const adminHolders: RoleHolderView[] = useMemo(
    () =>
      admins.flatMap((s) =>
        s.memberships.map((m) => ({
          userId: s.id,
          name: `${s.firstName} ${s.lastName}`.trim(),
          branchId: m.branchId,
          branchName: m.branchName ?? "",
          isDefault: false,
        })),
      ),
    [admins],
  );
  const hasStarter = roles.some((r) => r.starterKey !== null);
  const jobs = useMemo(() => new Map(staff.map((s) => [s.id, s.role])), [staff]);

  // Everyone still on the access from before roles, where a role now starts their job (R-D7).
  const current = roles.find((r) => r.kind === "CURRENT_ACCESS");
  const moves: Move[] = (current?.holderNames ?? [])
    .filter((h) => !h.isDefault)
    .map((h) => ({
      ...h,
      target: jobRole(roles, jobs.get(h.userId) ?? ""),
      self: h.userId === selfId,
    }))
    .filter((h) => h.target !== undefined && h.target.kind !== "CURRENT_ACCESS");
  const movable = moves.filter((m) => !m.self);

  const needle = query.trim().toLocaleLowerCase();
  const listed = [
    {
      id: ADMIN,
      name: "Administrator",
      jobs: "",
      people: admins.length,
      locked: "Always everything, at every location" as string | null,
    },
    ...roles.map((r) => ({
      id: r.id,
      name: r.name,
      jobs:
        r.defaultForJobs.length > 0
          ? r.defaultForJobs.map(jobLabel).join(", ")
          : r.isFallback
            ? "Any other job"
            : "",
      people: r.holders,
      locked: r.kind === "CURRENT_ACCESS" ? "Exactly the access from before roles" : null,
    })),
  ].filter((r) => needle === "" || r.name.toLocaleLowerCase().includes(needle));

  const rows: ShelfRow[] = listed.map((r) => {
    const on = r.id === selected;
    const open = (): void => {
      setSelected(on ? null : r.id);
      setConfirmArchive(false);
    };
    return {
      key: r.id,
      node: (
        <div
          role="button"
          tabIndex={0}
          className={on ? `${a.roleRow} ${a.roleRowOn}` : a.roleRow}
          aria-current={on ? "true" : undefined}
          onClick={open}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              open();
            }
          }}
          data-testid={`access-role-${r.name}`}
        >
          <span className={a.ellipsis}>{r.name}</span>
          <span className={`${a.ellipsis} ${a.muted}`}>{r.jobs === "" ? "—" : r.jobs}</span>
          <span className={a.right}>{r.people}</span>
          <span className={a.right}>
            {r.locked !== null ? (
              <LockTag title={r.locked} />
            ) : manage ? (
              <button
                type="button"
                className={`${p.ghost} ${a.small}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelected(r.id);
                  setDialog("allows");
                }}
                data-testid={`access-edit-${r.name}`}
              >
                Edit
              </button>
            ) : null}
          </span>
        </div>
      ),
    };
  });

  const role = roles.find((r) => r.id === selected) ?? null;
  const holders = selected === ADMIN ? adminHolders : (role?.holderNames ?? []);

  return (
    <>
      {!hasStarter && manage && (
        <div className={a.banner} data-testid="access-starter-offer">
          <span className={a.bannerText}>
            <b>Start from the usual roles.</b> Adds Practice manager, Dentist, Hygienist, Dental
            nurse, Receptionist and Finance officer, each yours to change, each starting a job.
            Everyone switched over keeps their access until you move them.
          </span>
          <button
            type="button"
            className={p.primary}
            disabled={writer.busy}
            onClick={() => setDialog("starter")}
            data-testid="access-install-starter"
          >
            Add the starter roles
          </button>
        </div>
      )}

      {hasStarter && assign && movable.length > 0 && (
        <div className={a.banner} data-testid="access-move-everyone">
          <span className={a.bannerText}>
            <b>{new Set(moves.map((m) => m.userId)).size} people</b> still have the access from
            before roles.
          </span>
          <button
            type="button"
            className={p.ghost}
            onClick={() => setDialog("move")}
            data-testid="access-move-open"
          >
            Move them onto their job&rsquo;s role
          </button>
        </div>
      )}

      <div ref={fillRef} className={a.fill} style={height > 0 ? { height } : undefined}>
        <ShelfList
          testId="access-role-list"
          head={
            <>
              <input
                type="search"
                className={p.search}
                placeholder="Search roles…"
                aria-label="Search roles"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                data-testid="access-role-search"
              />
              <span className={p.headCount}>
                {listed.length} roles{selected === null ? " · click one to see it" : ""}
              </span>
              {manage && (
                <button
                  type="button"
                  className={p.ghost}
                  style={{ marginLeft: "auto" }}
                  onClick={() => {
                    setSelected(null);
                    setDialog("new");
                  }}
                  data-testid="access-new-role"
                >
                  New role
                </button>
              )}
            </>
          }
          columns={
            <div className={a.roleCols} aria-hidden="true">
              <span>Role</span>
              <span>Default for job</span>
              <span className={a.right}>People</span>
              <span />
            </div>
          }
          rows={rows}
          empty="No role matches that."
          noun="roles"
          resetOn={query}
        />

        {selected !== null && (
          <RolePanel
            role={selected === ADMIN ? null : role}
            holders={holders}
            manage={manage}
            busy={writer.busy}
            onClose={() => setSelected(null)}
            onEdit={() => setDialog("allows")}
            onDetails={() => setDialog("details")}
            onArchive={() => setConfirmArchive(true)}
            onHolders={() => setDialog("holders")}
          />
        )}
      </div>

      {dialog === "allows" && role !== null && (
        <PermissionsDialog
          role={role}
          editable={manage && role.kind !== "CURRENT_ACCESS"}
          writer={writer}
          reload={reload}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "details" && role !== null && (
        <DetailsDialog
          role={role}
          roles={roles}
          jobsOf={jobs}
          writer={writer}
          reload={reload}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "new" && (
        <NewRoleDialog
          roles={roles}
          jobsOf={jobs}
          writer={writer}
          reload={reload}
          onClose={() => setDialog(null)}
          onMade={(id) => {
            setDialog(null);
            setSelected(id);
          }}
        />
      )}
      {dialog === "holders" && selected !== null && (
        <HoldersDialog
          title={selected === ADMIN ? "Administrator" : (role?.name ?? "")}
          holders={holders}
          jobs={jobs}
          selfId={selfId}
          everywhere={selected === ADMIN}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "move" && (
        <MoveDialog
          moves={moves}
          busy={writer.busy}
          onClose={() => setDialog(null)}
          onMove={() => {
            const result = { people: 0, places: 0 };
            void writer
              .run(
                async () => {
                  Object.assign(result, await moveToJobRoles());
                },
                reload,
                () => `${String(result.people)} people moved, at ${String(result.places)} places.`,
              )
              .then((ok) => {
                if (ok) setDialog(null);
              });
          }}
        />
      )}
      {dialog === "starter" && (
        <InBody>
          <ConfirmDialog
            title="Add the starter roles?"
            confirmLabel="Add the starter roles"
            busy={writer.busy}
            onCancel={() => setDialog(null)}
            onConfirm={() => {
              void writer
                .run(() => installStarterRoles(), reload, "The starter roles are added.")
                .then(() => setDialog(null));
            }}
            testId="access-starter-confirm"
          >
            Each starts a job, so anyone with that job and no role given at a location holds it
            there from now on.
            <AffectedList
              affected={STARTER_ROLES.filter(
                (s) => s.locked !== true && !roles.some((r) => r.starterKey === s.key),
              ).flatMap((s) => jobMoveAffected(roles, jobs, null, [...s.defaultFor], s.name))}
            />
          </ConfirmDialog>
        </InBody>
      )}
      {confirmArchive && role !== null && (
        <InBody>
          <ConfirmDialog
            title={`Archive “${role.name}”?`}
            confirmLabel="Archive"
            tone="danger"
            busy={writer.busy}
            onCancel={() => setConfirmArchive(false)}
            onConfirm={() => {
              void writer
                .run(() => archiveRole(role.id), reload, `“${role.name}” is archived.`)
                .then((ok) => {
                  setConfirmArchive(false);
                  if (ok) setSelected(null);
                });
            }}
            testId="access-archive-confirm"
          >
            Nobody holds it, so nothing changes for anyone.
          </ConfirmDialog>
        </InBody>
      )}
    </>
  );
}

function RolePanel({
  role,
  holders,
  manage,
  busy,
  onClose,
  onEdit,
  onDetails,
  onArchive,
  onHolders,
}: {
  /** Null is Administrator, which is not a row of its own (RB9). */
  role: AccessRoleView | null;
  holders: readonly RoleHolderView[];
  manage: boolean;
  busy: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDetails: () => void;
  onArchive: () => void;
  onHolders: () => void;
}): React.JSX.Element {
  const effective = useMemo(
    () =>
      role === null ? new Set<Permission>(ALL_PERMISSIONS) : withClosure(rolePermissions(role)),
    [role],
  );
  const people = new Set(holders.map((h) => h.userId)).size;
  const locked = role === null || role.kind === "CURRENT_ACCESS";
  const title = role?.name ?? "Administrator";
  const sub =
    role === null
      ? "Locked · every location · it cannot be edited or archived, and the clinic always keeps at least one."
      : role.kind === "CURRENT_ACCESS"
        ? "Access from before roles · locked"
        : (role.description ?? "");

  return (
    <aside className={p.panel} aria-label={title} data-testid="access-role-panel">
      <div className={p.panelHead}>
        <h2 className={p.panelTitle}>{title}</h2>
        <button type="button" className={p.close} onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      {sub !== "" && <p className={p.panelSub}>{sub}</p>}

      <div className={p.panelBody}>
        {/* One line, not a large figure, so the panel fits a 1366 × 768 window without scrolling. */}
        <p className={a.panelCount} data-testid="access-panel-count">
          <b>{effective.size}</b> of {ALL_PERMISSIONS.length} things allowed
          {role !== null && (
            <span className={a.muted}>
              {" "}
              · default for{" "}
              {role.defaultForJobs.length > 0
                ? role.defaultForJobs.map(jobLabel).join(", ")
                : role.isFallback
                  ? "any job no other role starts"
                  : "no job"}
            </span>
          )}
        </p>

        <section className={a.panelSection}>
          <h3 className={a.sectionTitle}>What it allows</h3>
          <LevelList held={(permission) => effective.has(permission)} />
        </section>

        <section className={a.panelSection}>
          <h3 className={a.sectionTitle}>Who holds it</h3>
          <div className={a.holdLine}>
            <span>
              <b>{people}</b> {people === 1 ? "person" : "people"}
              {holders.length > 0 && <span className={a.muted}> · {holdSummary(holders)}</span>}
            </span>
            {holders.length > 0 && (
              <button
                type="button"
                className={`${p.ghost} ${a.small}`}
                onClick={onHolders}
                data-testid="access-holders-open"
              >
                See who
              </button>
            )}
          </div>
          {role === null && (
            <p className={a.hint}>Make somebody an administrator on their page in Staff.</p>
          )}
        </section>
      </div>

      {manage && role !== null && (
        <div className={p.panelTools}>
          <button
            type="button"
            className={p.primary}
            disabled={busy || locked}
            title={locked ? "Locked: exactly what everyone could do before roles" : undefined}
            onClick={onEdit}
            data-testid="access-edit-permissions"
          >
            {locked ? "Edit permissions (locked)" : "Edit permissions"}
          </button>
          <button
            type="button"
            className={p.ghost}
            disabled={busy}
            onClick={onDetails}
            data-testid="access-edit-details"
          >
            Edit details
          </button>
          {!role.isFallback && (
            <button
              type="button"
              className={p.quiet}
              disabled={busy || people > 0}
              title={people > 0 ? "Give the people who hold it another role first" : undefined}
              onClick={onArchive}
              data-testid="access-role-archive"
            >
              Archive
            </button>
          )}
        </div>
      )}
    </aside>
  );
}

/**
 * What a role allows — **one table, no scrolling** (the client, 2026-10-04):
 * each part of DentaCore down the side, None / View / Full across, and the
 * single actions of whichever part is picked beside it.
 */
function PermissionsDialog({
  role,
  editable,
  writer,
  reload,
  onClose,
}: {
  role: AccessRoleView;
  editable: boolean;
  writer: Writer;
  reload: () => Promise<void>;
  onClose: () => void;
}): React.JSX.Element {
  const [listed, setListed] = useState<ReadonlySet<Permission>>(new Set(rolePermissions(role)));
  const implied = useMemo(() => impliedBy(listed), [listed]);
  const held = (permission: Permission): boolean =>
    listed.has(permission) || implied.has(permission) || FLOOR_SET.has(permission);
  const [part, setPart] = useState<string>(
    () => MODULES.find((m) => moduleChoice(held, m.key) === "custom")?.key ?? MODULES[0]?.key ?? "",
  );
  const before = useMemo(() => withClosure(rolePermissions(role)), [role]);
  const dirty = [...listed].sort().join(",") !== [...rolePermissions(role)].sort().join(",");
  const people = new Set(role.holderNames.map((h) => h.userId)).size;
  const current = MODULES.find((m) => m.key === part) ?? MODULES[0];

  return (
    <InBody>
      <Modal label={`What ${role.name} allows`} onClose={onClose} panelClassName={a.dialogWide}>
        <h2 className={p.dialogTitle}>What &ldquo;{role.name}&rdquo; allows</h2>
        <div className={a.permGrid}>
          <table className={a.ptable} data-testid="access-perm-table">
            <thead>
              <tr>
                <th className={a.partCell}>Part of DentaCore</th>
                <th>None</th>
                <th>View</th>
                <th>Full</th>
                <th />
                <th className={a.countCell}>Allowed</th>
              </tr>
            </thead>
            <tbody>
              {MODULES.map((module) => {
                const all = choosableIn(module.key);
                const views = viewsIn(module.key);
                const choice = moduleChoice(held, module.key);
                const hasView = views.length > 0 && views.length < all.length;
                const radio = (option: "none" | "view" | "full"): React.JSX.Element => (
                  <td>
                    {option === "view" && !hasView ? (
                      <span className={a.na} title="Nothing separate to view here">
                        —
                      </span>
                    ) : (
                      <input
                        type="radio"
                        name={`part-${module.key}`}
                        aria-label={`${module.label}: ${option}`}
                        checked={choice === option}
                        disabled={!editable}
                        onChange={() => {
                          setPart(module.key);
                          setListed(withChoice(listed, module.key, option));
                        }}
                        data-testid={`access-choice-${module.key}-${option}`}
                      />
                    )}
                  </td>
                );
                return (
                  <tr key={module.key} className={part === module.key ? a.partOn : undefined}>
                    <td className={a.partCell}>
                      <button
                        type="button"
                        className={a.partName}
                        title={module.summary}
                        onClick={() => setPart(module.key)}
                        data-testid={`access-part-${module.key}`}
                      >
                        {module.label}
                      </button>
                    </td>
                    {radio("none")}
                    {radio("view")}
                    {radio("full")}
                    <td>
                      {choice === "custom" && (
                        <span className={a.custom} title="Some actions picked by hand">
                          ◐ Custom
                        </span>
                      )}
                    </td>
                    <td className={a.countCell}>
                      {all.filter(held).length}/{all.length}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {current !== undefined && (
            <div className={a.detail} data-testid="access-perm-detail">
              <p className={a.detailTitle}>
                {current.label} <span>· {current.summary}</span>
              </p>
              <ul className={a.checks}>
                {permissionsIn(current.key).map((permission) => {
                  const d = def(permission);
                  const carrier = implied.get(permission);
                  const floor = FLOOR_SET.has(permission);
                  const fixed =
                    !editable || floor || (carrier !== undefined && !listed.has(permission));
                  const note = floor
                    ? "Everybody has this — allergies are never hidden"
                    : carrier !== undefined && !listed.has(permission)
                      ? `Comes with “${def(carrier).label}”`
                      : null;
                  return (
                    <li key={permission}>
                      <label className={a.check}>
                        <input
                          type="checkbox"
                          checked={held(permission)}
                          disabled={fixed}
                          onChange={() => {
                            const next = new Set(listed);
                            if (next.has(permission)) next.delete(permission);
                            else next.add(permission);
                            setListed(next);
                          }}
                          data-testid={`access-perm-${permission}`}
                        />
                        <span>
                          {d.label}
                          {d.risk !== "standard" && (
                            <span className={a.tagWarm}>
                              {d.risk === "critical" ? "Role only" : "Sensitive"}
                            </span>
                          )}
                          {note !== null && <span className={a.checkNote}>{note}</span>}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <ImpactLine
          impact={impactOf(before, withClosure(listed))}
          lead={
            <>
              Saving changes this for{" "}
              <b>
                {people} {people === 1 ? "person" : "people"}
              </b>{" "}
              wherever they hold it: they
            </>
          }
        />
        <div className={p.dialogActions}>
          <button type="button" className={p.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={p.primary}
            disabled={!editable || !dirty || writer.busy}
            onClick={() => {
              void writer
                .run(
                  () => updateRole(role.id, { permissions: [...listed] }),
                  reload,
                  `“${role.name}” is saved. Everyone who holds it has the change now.`,
                )
                .then((ok) => {
                  if (ok) onClose();
                });
            }}
            data-testid="access-role-save"
          >
            Save
          </button>
        </div>
      </Modal>
    </InBody>
  );
}

/** The jobs a role starts, with the role each one would be taken from said before saving. */
function JobChips({
  jobs,
  setJobs,
  others,
  verb,
}: {
  jobs: readonly Job[];
  setJobs: (jobs: Job[]) => void;
  others: readonly AccessRoleView[];
  verb: string;
}): React.JSX.Element {
  const owner = (job: Job): string | undefined =>
    others.find((r) => r.defaultForJobs.includes(job))?.name;
  const taking = jobs.flatMap((job) => {
    const from = owner(job);
    return from === undefined ? [] : [`${jobLabel(job)} from “${from}”`];
  });
  return (
    <div className={p.field}>
      <span className={p.fieldLabel}>New staff with these jobs start with it</span>
      <div className={a.chips}>
        {ROLES.map((job) => {
          const on = jobs.includes(job);
          const from = owner(job);
          return (
            <button
              key={job}
              type="button"
              className={a.chip}
              aria-pressed={on}
              title={!on && from !== undefined ? `Now starts with "${from}"` : undefined}
              onClick={() => setJobs(on ? jobs.filter((j) => j !== job) : [...jobs, job])}
              data-testid={`access-job-${job}`}
            >
              {jobLabel(job)}
            </button>
          );
        })}
      </div>
      {taking.length > 0 && (
        <p className={a.notice} data-testid="access-job-taking">
          {verb} moves {taking.join(" and ")}. Each job starts with one role.
        </p>
      )}
    </div>
  );
}

function DetailsDialog({
  role,
  roles,
  jobsOf,
  writer,
  reload,
  onClose,
}: {
  role: AccessRoleView;
  roles: readonly AccessRoleView[];
  jobsOf: ReadonlyMap<string, string>;
  writer: Writer;
  reload: () => Promise<void>;
  onClose: () => void;
}): React.JSX.Element {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? "");
  const [jobs, setJobs] = useState<Job[]>(role.defaultForJobs);
  const others = roles.filter((r) => r.id !== role.id);
  const dirty =
    name.trim() !== role.name ||
    (description.trim() || null) !== (role.description ?? null) ||
    [...jobs].sort().join(",") !== [...role.defaultForJobs].sort().join(",");

  return (
    <InBody>
      <Modal label={`Edit ${role.name}`} onClose={onClose}>
        <h2 className={p.dialogTitle}>Edit &ldquo;{role.name}&rdquo;</h2>
        <label className={p.field}>
          <span className={p.fieldLabel}>
            Name
            <RequiredMark />
          </span>
          <input
            className={p.input}
            value={name}
            onChange={(event) => setName(event.target.value)}
            data-testid="access-role-name"
          />
        </label>
        <label className={p.field}>
          <span className={p.fieldLabel}>What it is for</span>
          <textarea
            className={p.input}
            style={{ height: 64, paddingTop: 8 }}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            data-testid="access-role-description"
          />
        </label>
        <JobChips jobs={jobs} setJobs={setJobs} others={others} verb="Saving" />
        <p className={a.hint}>
          Anyone with these jobs holds this role wherever nobody gave them one — new staff, and
          anybody at a location with no role of their own. Changing the jobs changes their access
          now.
        </p>
        {[...jobs].sort().join(",") !== [...role.defaultForJobs].sort().join(",") && (
          <AffectedList
            affected={jobMoveAffected(roles, jobsOf, role.id, jobs, name.trim() || role.name)}
          />
        )}
        <div className={p.dialogActions}>
          <button type="button" className={p.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={p.primary}
            disabled={!dirty || name.trim() === "" || writer.busy}
            onClick={() => {
              void writer
                .run(
                  () =>
                    updateRole(role.id, {
                      name: name.trim(),
                      description: description.trim() === "" ? null : description.trim(),
                      defaultForJobs: jobs,
                    }),
                  reload,
                  `“${name.trim()}” is saved.`,
                )
                .then((ok) => {
                  if (ok) onClose();
                });
            }}
            data-testid="access-details-save"
          >
            Save
          </button>
        </div>
      </Modal>
    </InBody>
  );
}

/** New role — a dialog, the way New appointment is. What it allows comes after, with Edit permissions. */
function NewRoleDialog({
  roles,
  jobsOf,
  writer,
  reload,
  onClose,
  onMade,
}: {
  roles: readonly AccessRoleView[];
  jobsOf: ReadonlyMap<string, string>;
  writer: Writer;
  reload: () => Promise<void>;
  onClose: () => void;
  onMade: (id: string) => void;
}): React.JSX.Element {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [from, setFrom] = useState("");
  const [jobs, setJobs] = useState<Job[]>([]);
  const source = roles.find((r) => r.id === from);

  return (
    <InBody>
      <Modal label="New role" onClose={onClose} testId="access-new-role-dialog">
        <h2 className={p.dialogTitle}>New role</h2>
        <label className={p.field}>
          <span className={p.fieldLabel}>
            Name
            <RequiredMark />
          </span>
          <input
            className={p.input}
            value={name}
            placeholder="Front desk lead"
            onChange={(event) => setName(event.target.value)}
            data-testid="access-new-name"
          />
        </label>
        <label className={p.field}>
          <span className={p.fieldLabel}>What it is for</span>
          <textarea
            className={p.input}
            style={{ height: 64, paddingTop: 8 }}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        <label className={p.field}>
          <span className={p.fieldLabel}>Start from</span>
          <select
            className={p.input}
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            data-testid="access-new-from"
          >
            <option value="">Nothing — I&rsquo;ll choose what it allows</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                A copy of what &ldquo;{r.name}&rdquo; allows
              </option>
            ))}
          </select>
        </label>
        <JobChips jobs={jobs} setJobs={setJobs} others={roles} verb="Making it" />
        {jobs.length > 0 && (
          <AffectedList
            affected={jobMoveAffected(roles, jobsOf, null, jobs, name.trim() || "the new role")}
          />
        )}
        <p className={a.hint}>
          Once made, it opens beside the list. Give it to people on their page in Staff.
        </p>
        <div className={p.dialogActions}>
          <button type="button" className={p.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={p.primary}
            disabled={name.trim() === "" || writer.busy}
            onClick={() => {
              const made: { id: string | null } = { id: null };
              void writer
                .run(
                  async () => {
                    made.id = (
                      await createRole({
                        name: name.trim(),
                        description: description.trim() === "" ? null : description.trim(),
                        permissions: source === undefined ? [] : rolePermissions(source),
                        defaultForJobs: jobs,
                      })
                    ).id;
                  },
                  reload,
                  `“${name.trim()}” is made. Choose what it allows with Edit permissions.`,
                )
                .then((ok) => {
                  if (ok && made.id !== null) onMade(made.id);
                });
            }}
            data-testid="access-new-make"
          >
            Make role
          </button>
        </div>
      </Modal>
    </InBody>
  );
}

/** Who holds a role — a count in the panel, the names here: searchable, paged. */
function HoldersDialog({
  title,
  holders,
  jobs,
  selfId,
  everywhere,
  onClose,
}: {
  title: string;
  holders: readonly RoleHolderView[];
  jobs: ReadonlyMap<string, string>;
  selfId: string | null;
  everywhere: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const needle = query.trim().toLocaleLowerCase();
  const rows = holders.filter(
    (h) =>
      needle === "" ||
      h.name.toLocaleLowerCase().includes(needle) ||
      h.branchName.toLocaleLowerCase().includes(needle) ||
      jobLabel(jobs.get(h.userId) ?? "")
        .toLocaleLowerCase()
        .includes(needle),
  );
  const { shown, pager } = usePages(rows, 8, "places", needle);
  const cols = {
    "--cols": "minmax(0,1.2fr) minmax(0,1fr) minmax(0,0.8fr) minmax(0,1fr)",
  } as React.CSSProperties;

  return (
    <InBody>
      <Modal label={`Who holds ${title}`} onClose={onClose} panelClassName={a.dialogMid}>
        <h2 className={p.dialogTitle}>Who holds &ldquo;{title}&rdquo;</h2>
        <div className={a.toolbar}>
          <input
            type="search"
            className={p.search}
            placeholder="Search by name, job or location…"
            aria-label="Search holders"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <span className={p.headCount}>
            {rows.length} of {holders.length}
          </span>
        </div>
        <div className={a.table} data-testid="access-holders">
          <div className={a.tableHead} style={cols}>
            <span>Name</span>
            <span>Location</span>
            <span>Job</span>
            <span>How</span>
          </div>
          {shown.map((h) => (
            <button
              key={`${h.userId}:${h.branchId}`}
              type="button"
              className={a.tableRow}
              style={cols}
              onClick={() => navigate(`/people/staff/${h.userId}`)}
            >
              <span className={a.ellipsis}>
                {h.name}
                {h.userId === selfId && <span className={a.muted}> (you)</span>}
              </span>
              <span className={`${a.ellipsis} ${a.muted}`}>{h.branchName}</span>
              <span className={a.muted}>{jobLabel(jobs.get(h.userId) ?? "")}</span>
              <span className={`${a.ellipsis} ${a.muted}`}>
                {everywhere ? "Every location" : h.isDefault ? "Their job's role" : "Given"}
              </span>
            </button>
          ))}
          {rows.length === 0 && <p className={p.blank}>Nobody matches.</p>}
        </div>
        {pager}
        <p className={a.hint}>
          Click someone to open their page, where roles are given per location.
        </p>
        <div className={p.dialogActions}>
          <button type="button" className={p.ghost} onClick={onClose}>
            Close
          </button>
        </div>
      </Modal>
    </InBody>
  );
}

function MoveDialog({
  moves,
  busy,
  onClose,
  onMove,
}: {
  moves: Move[];
  busy: boolean;
  onClose: () => void;
  onMove: () => void;
}): React.JSX.Element {
  const { shown, pager } = usePages(moves, 8, "places");
  const movable = moves.filter((m) => !m.self).length;
  const cols = { "--cols": "minmax(0,1.2fr) minmax(0,1fr) minmax(0,1.2fr)" } as React.CSSProperties;
  return (
    <InBody>
      <Modal
        label="Move people onto their job's role"
        onClose={onClose}
        panelClassName={a.dialogMid}
      >
        <h2 className={p.dialogTitle}>Move people onto their job&rsquo;s role</h2>
        <div className={a.table} data-testid="access-move-list">
          <div className={a.tableHead} style={cols}>
            <span>Name</span>
            <span>Location</span>
            <span>Moves to</span>
          </div>
          {shown.map((m) => (
            <div key={`${m.userId}:${m.branchId}`} className={a.tableRow} style={cols}>
              <span className={a.ellipsis}>{m.name}</span>
              <span className={`${a.ellipsis} ${a.muted}`}>{m.branchName}</span>
              <span className={a.muted}>
                {m.self
                  ? "Stays — another administrator has to move you"
                  : `→ ${m.target?.name ?? ""}`}
              </span>
            </div>
          ))}
        </div>
        {pager}
        <p className={a.hint}>
          Each person moves at each location where they still have the old access. They lose
          anything their new role does not carry; you can give anything back on their page.
        </p>
        <div className={p.dialogActions}>
          <button type="button" className={p.ghost} onClick={onClose}>
            Not now
          </button>
          <button
            type="button"
            className={p.primary}
            disabled={busy || movable === 0}
            onClick={onMove}
            data-testid="access-move-confirm"
          >
            Move {movable} {movable === 1 ? "place" : "places"}
          </button>
        </div>
      </Modal>
    </InBody>
  );
}

// ── Exceptions ────────────────────────────────────────────────────────────────

function ExceptionsTab({
  report,
  selfId,
  writer,
  reload,
}: {
  report: OverridesReport | null;
  selfId: string | null;
  writer: Writer;
  reload: () => Promise<void>;
}): React.JSX.Element {
  const [selected, setSelected] = useState<string | null>(null);
  const [fillRef, height] = useFill();
  if (report === null) return <p className={a.hint}>Reading&hellip;</p>;

  const open: AccessOverrideView | null = report.overrides.find((o) => o.id === selected) ?? null;
  const cols = { gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr) 96px" };
  const rows: ShelfRow[] = report.overrides.map((o) => {
    const on = o.id === selected;
    return {
      key: o.id,
      node: (
        <button
          type="button"
          className={on ? `${a.roleRow} ${a.roleRowOn}` : a.roleRow}
          style={cols}
          onClick={() => setSelected(on ? null : o.id)}
          data-testid="access-override-row"
        >
          <span className={a.ellipsis}>
            <span className={o.effect === "grant" ? a.grant : a.revoke}>
              {o.effect === "grant" ? "Given" : "Taken away"}
            </span>
            {o.userName} · {def(o.permission).label}
          </span>
          <span className={`${a.ellipsis} ${a.muted}`}>{o.reason}</span>
          <span className={`${a.right} ${a.muted}`}>
            {o.expiresAt === null ? "—" : shortDate(o.expiresAt)}
          </span>
        </button>
      ),
    };
  });

  return (
    <>
      {report.shared.map((group) => (
        <p
          key={`${group.effect}:${group.permission}`}
          className={a.notice}
          style={{ marginBottom: 12 }}
        >
          {group.people} people have the same exception —{" "}
          {group.effect === "grant" ? "given" : "taken away"}: {def(group.permission).label}. That
          is probably a role worth making.
        </p>
      ))}
      <div ref={fillRef} className={a.fill} style={height > 0 ? { height } : undefined}>
        <ShelfList
          testId="access-overrides"
          head={
            <span className={p.headCount}>
              {report.overrides.length} live · add one on a person&rsquo;s page in Staff
            </span>
          }
          columns={
            <div className={a.roleCols} style={cols} aria-hidden="true">
              <span>Person and exception</span>
              <span>Why</span>
              <span className={a.right}>Until</span>
            </div>
          }
          rows={rows}
          empty="No exceptions. Everybody has exactly what their roles give them."
          noun="exceptions"
        />
        {open !== null && (
          <aside className={p.panel} aria-label={open.userName}>
            <div className={p.panelHead}>
              <h2 className={p.panelTitle}>{open.userName}</h2>
              <button
                type="button"
                className={p.close}
                onClick={() => setSelected(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className={p.panelSub}>
              {open.effect === "grant" ? "Given" : "Taken away"} by {open.grantedByName} · every
              location
            </p>
            <div className={p.panelBody}>
              <section className={a.panelSection}>
                <h3 className={a.sectionTitle}>{def(open.permission).label}</h3>
                <p className={a.plain}>
                  &ldquo;{open.reason}&rdquo; ·{" "}
                  {open.expiresAt === null ? "no end date" : `until ${shortDate(open.expiresAt)}`}
                </p>
              </section>
            </div>
            <div className={p.panelTools}>
              <Link className={p.ghost} to={`/people/staff/${open.userId}`}>
                Open their page
              </Link>
              {open.userId !== selfId && (
                <button
                  type="button"
                  className={p.quiet}
                  disabled={writer.busy}
                  onClick={() => {
                    void writer
                      .run(() => endOverride(open.id), reload, "The exception has ended.")
                      .then((ok) => {
                        if (ok) setSelected(null);
                      });
                  }}
                  data-testid="access-override-end"
                >
                  End now
                </button>
              )}
            </div>
          </aside>
        )}
      </div>
    </>
  );
}

// ── Who can… ──────────────────────────────────────────────────────────────────

/**
 * Who can… (2026-10-04): pick one action, see one row per person with a column
 * per location — "✓ Receptionist", "✕ No" or "Doesn't work here".
 */
function WhoCanTab({ fail }: { fail: (message: string) => void }): React.JSX.Element {
  const navigate = useNavigate();
  const [permission, setPermission] = useState<Permission>("billing.invoice.void");
  const [everyone, setEveryone] = useState(false);
  const [view, setView] = useState<WhoCanView | null>(null);
  const [fillRef, height] = useFill();

  useEffect(() => {
    const controller = new AbortController();
    setView(null);
    whoCan(permission, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setView(result);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) fail(describe(cause, "Could not work out who can."));
      });
    return () => controller.abort();
  }, [permission, fail]);

  const people = (view?.people ?? []).filter(
    (person) => everyone || person.at.some((x) => x.held === true),
  );
  const branchCols = (view?.branches ?? []).map(() => "minmax(0,1fr)").join(" ");
  const cols = { gridTemplateColumns: `minmax(0,1.1fr) minmax(0,0.7fr) ${branchCols}` };

  const rows: ShelfRow[] = people.map((person) => ({
    key: person.userId,
    node: (
      <button
        type="button"
        className={a.roleRow}
        style={cols}
        onClick={() => navigate(`/people/staff/${person.userId}`)}
        data-testid="access-whocan-row"
      >
        <span className={a.ellipsis}>{person.name}</span>
        <span className={a.muted}>{jobLabel(person.job)}</span>
        {person.at.map((cell) => (
          <span key={cell.branchId} className={a.ellipsis}>
            {cell.held === null ? (
              <span className={a.away}>Doesn&rsquo;t work here</span>
            ) : cell.held ? (
              <span className={a.yes}>✓ {cell.because}</span>
            ) : (
              <span className={a.no}>✕ No</span>
            )}
          </span>
        ))}
      </button>
    ),
  }));

  return (
    <div ref={fillRef} className={a.fill} style={height > 0 ? { height } : undefined}>
      <ShelfList
        testId="access-whocan"
        head={
          <>
            <label className={a.whoPick}>
              <span>Who can</span>
              <select
                className={p.input}
                value={permission}
                onChange={(event) => setPermission(event.target.value as Permission)}
                aria-label="Choose an action"
                data-testid="access-whocan-pick"
              >
                {MODULES.map((module) => (
                  <optgroup key={module.key} label={module.label}>
                    {permissionsIn(module.key).map((key) => (
                      <option key={key} value={key}>
                        {def(key).label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <span>?</span>
            </label>
            <span className={a.segmented} role="group" aria-label="Show">
              <button
                type="button"
                className={a.segment}
                aria-pressed={!everyone}
                onClick={() => setEveryone(false)}
              >
                Only who can
              </button>
              <button
                type="button"
                className={a.segment}
                aria-pressed={everyone}
                onClick={() => setEveryone(true)}
              >
                Everyone
              </button>
            </span>
          </>
        }
        columns={
          <div className={a.roleCols} style={cols} aria-hidden="true">
            <span>Name</span>
            <span>Job</span>
            {(view?.branches ?? []).map((b, index) => (
              <span key={b.id} className={a.ellipsis}>
                {locationShort(b.name)} ·{" "}
                {view?.people.filter((x) => x.at[index]?.held === true).length ?? 0}
              </span>
            ))}
          </div>
        }
        rows={rows}
        empty={view === null ? "Reading…" : "Nobody can do this at any location."}
        noun="people"
        resetOn={`${permission}|${String(everyone)}`}
      />
    </div>
  );
}

export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
